import React, { useEffect, useState } from 'react';
import * as payrollService from '../../services/payrollService';
import useUIStore from '../../store/uiStore';
import PayrollTabs from '../../components/PayrollTabs';
import { TrendLineChart, GroupedBarChart, identity } from '../../components/PayrollCharts';

const now = new Date();

const PayrollDashboardPage = () => {
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [costTrend, setCostTrend] = useState(null);
  const [branchSplit, setBranchSplit] = useState(null);
  const [attendanceTrend, setAttendanceTrend] = useState(null);
  const [avgHours, setAvgHours] = useState(null);
  const [loading, setLoading] = useState(true);
  const showToast = useUIStore((s) => s.showToast);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      payrollService.getCostTrend({ months: 12 }),
      payrollService.getBranchSplit({ month, year }),
      payrollService.getAttendanceTrend({ months: 12 }),
      payrollService.getAvgHoursByDepartment({ month, year }),
    ])
      .then(([trendRes, branchRes, attTrendRes, hoursRes]) => {
        setCostTrend(trendRes.data.points || []);
        setBranchSplit(branchRes.data.branches || []);
        setAttendanceTrend(attTrendRes.data.points || []);
        setAvgHours(hoursRes.data.departments || []);
      })
      .catch((err) => showToast(err.response?.data?.detail || 'Failed to load dashboard', 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year]);

  const branchData = (branchSplit || []).map((b) => ({ label: b.branch, gross: b.gross, net: b.net }));

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        <PayrollTabs />

        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="text-xl font-bold text-white">HR Payroll Dashboard</h1>
          <div className="flex items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-white/70 mb-1">Month</label>
              <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="border border-gray-300 rounded px-3 py-1.5 text-sm">
                {['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].slice(1).map((m, i) => (
                  <option key={m} value={i + 1}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-white/70 mb-1">Year</label>
              <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-24 border border-gray-300 rounded px-3 py-1.5 text-sm" />
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="glass-card p-6">
            <h2 className="font-semibold text-gray-900 mb-1">Monthly Payroll Cost Trend</h2>
            <p className="text-xs text-gray-400 mb-3">Gross vs net payout across payroll runs, most recent 12.</p>
            {loading ? <p className="text-sm text-gray-400 py-10 text-center">Loading...</p> : (
              <TrendLineChart
                points={costTrend}
                series={[
                  { key: 'gross', label: 'Gross', color: '#2F6690' },
                  { key: 'net', label: 'Net', color: '#22c55e' },
                ]}
                emptyLabel="No payroll runs yet."
              />
            )}
          </div>

          <div className="glass-card p-6">
            <h2 className="font-semibold text-gray-900 mb-1">Branch / Department Cost Split</h2>
            <p className="text-xs text-gray-400 mb-3">Gross vs net cost per branch, for {month}/{year} - all runs in that period.</p>
            {loading ? <p className="text-sm text-gray-400 py-10 text-center">Loading...</p> : (
              <GroupedBarChart
                data={branchData}
                series={[
                  { key: 'gross', label: 'Gross', color: '#2F6690' },
                  { key: 'net', label: 'Net', color: '#22c55e' },
                ]}
                emptyLabel="No payroll run for this month yet."
              />
            )}
          </div>

          <div className="glass-card p-6">
            <h2 className="font-semibold text-gray-900 mb-1">Late Comings / Absenteeism Trend</h2>
            <p className="text-xs text-gray-400 mb-3">Total late-arrival days and loss-of-pay (absent) days across everyone, most recent 12 months.</p>
            {loading ? <p className="text-sm text-gray-400 py-10 text-center">Loading...</p> : (
              <GroupedBarChart
                data={(attendanceTrend || []).map((p) => ({ label: p.label, late_count: p.late_count, lop_days: p.lop_days }))}
                series={[
                  { key: 'late_count', label: 'Late days', color: '#F97316' },
                  { key: 'lop_days', label: 'LOP (absent) days', color: '#DC2626' },
                ]}
                valueFormatter={identity}
                emptyLabel="No attendance recorded yet - enter late_count/lop_days on each month's attendance."
              />
            )}
          </div>

          <div className="glass-card p-6">
            <h2 className="font-semibold text-gray-900 mb-1">Avg Hours Worked / Day by Department</h2>
            <p className="text-xs text-gray-400 mb-3">Total hours worked ÷ present days, by payroll department, for {month}/{year}.</p>
            {loading ? <p className="text-sm text-gray-400 py-10 text-center">Loading...</p> : (
              <GroupedBarChart
                data={(avgHours || []).map((d) => ({ label: d.department, avg_hours_per_day: d.avg_hours_per_day }))}
                series={[{ key: 'avg_hours_per_day', label: 'Avg hrs/day', color: '#7C3AED' }]}
                valueFormatter={identity}
                emptyLabel="No hours_worked recorded for this month yet."
              />
            )}
          </div>
        </div>

        <p className="text-xs text-white/50 mt-4">
          The last two charts need "Late Days" and "Hours Worked" entered on the monthly attendance record
          (Payroll Run → Attendance table, or a future punch-in/GPS import) - they're optional fields, so a
          month with none entered will show empty.
        </p>
      </div>
    </div>
  );
};

export default PayrollDashboardPage;
